import { memo, useMemo } from 'react';
import classNames from 'classnames';
import { StreamType } from '@vgtv/api-client/lib/component';
import { useTranslation } from 'react-i18next';
import {
  OpenExternal as OpenExternalIcon,
  Publish as PublishIcon,
} from '@vgno/icons';

import { AssetLink } from '../../AssetLink/AssetLink';
import { AssetThumbnail } from '../AssetThumbnail';
import { Title } from '../shared/Title/Title';
import { Description } from '../shared/Description/Description';
import { useAppSelector } from '../../../modules/hooks';
import { selectAssetById } from '../../../modules/assets';
import {
  formatAssetUpcomingDate,
  getAssetAgeLimit,
  getSubscriptionLabel,
  isAssetSponsored,
  isAssetUpcoming,
  isCurrentlyLive,
  isDocumentary,
  isLiveStream,
  isTVShowEpisode,
} from '../../../shared/helpers/assetHelpers';
import { useServerTime } from '../../utils/useServerTime';
import { selectCurrentlyPlayingAssetId } from '../../../modules/ui';
import { useAssetWatchtimeData } from '../../utils/useAssetWatchtimeData';
import { useVendor } from '../../VendorPovider/VendorPovider';
import { PromoBadge } from '../../PromoBadge/PromoBadge';
import { useAssetRelease } from '../../utils/useAssetRelease';
import { Label } from '../../Label/Label';

import type { ImageSources } from '../../../helpers/images';
import type { UnixTimestamp } from '@vgtv/api-client';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { ReactNode, VFC } from 'react';

import * as styles from './AssetVertical.module.scss';

export interface AssetVerticalProps {
  id: SvpAssetAdditional['id'];
  title: string;
  description?: string | null;
  imageSources?: ImageSources;
  publishedDate?: UnixTimestamp | null;
  badge?: ReactNode;
  href?: string;
  onClick?: () => void;
  labels?: string[];
  skipMetadata?: boolean;
  onHover?: () => void;
  replacePath?: boolean;
}

const getPromoVariant = (asset: SvpAssetAdditional) => {
  if (isTVShowEpisode(asset)) {
    return 'episode';
  }

  if (isDocumentary(asset)) {
    return 'documentary';
  }

  return undefined;
};

const defaultLabels: string[] = [];

export const AssetVerticalBase: VFC<AssetVerticalProps> = ({
  id,
  title,
  description,
  imageSources,
  badge,
  href,
  onClick,
  labels = defaultLabels,
  skipMetadata = false,
  onHover,
  replacePath,
}) => {
  const { serverTime } = useServerTime();
  const { vendor } = useVendor();
  const asset = useAppSelector((state) => selectAssetById(state, vendor, id))!;
  const { position, isWatched } = useAssetWatchtimeData(id, vendor);
  const { isNewlyReleased } = useAssetRelease(asset);

  const promoVariant =
    isNewlyReleased && asset ? getPromoVariant(asset) : undefined;

  const isPlaying = asset?.id === useAppSelector(selectCurrentlyPlayingAssetId);
  const isExternal = href !== undefined;
  const isLive = asset && isCurrentlyLive(asset, serverTime);

  const { t } = useTranslation();

  const upcomingAssetLabel = useMemo(
    () =>
      asset &&
      isAssetUpcoming(asset) &&
      !isLiveStream(asset) && (
        <div className={styles.upcoming}>
          <PublishIcon />
          {t('Coming')}
          <span>{formatAssetUpcomingDate(asset)}</span>
        </div>
      ),
    [asset, t]
  );

  if (!asset) {
    return null;
  }

  const duration =
    asset.duration &&
    ![StreamType.LIVE, StreamType.WAS_LIVE].includes(
      asset.streamType as StreamType
    )
      ? asset.duration / 1000
      : undefined;

  const thumbnail = onHover ? (
    <div className={styles.container} onMouseOver={onHover} onFocus={onHover}>
      <AssetThumbnail
        imageSources={imageSources}
        className={styles.thumbnail}
        badge={badge}
        ageLimit={getAssetAgeLimit(asset)}
        secondaryBadge={
          imageSources?.aspectRatio === 'horizontal' &&
          promoVariant && <PromoBadge variant={promoVariant} />
        }
      />
    </div>
  ) : (
    <div className={styles.container}>
      <AssetThumbnail
        imageSources={imageSources}
        className={styles.thumbnail}
        badge={badge}
        secondaryBadge={
          imageSources?.aspectRatio === 'horizontal' &&
          promoVariant && <PromoBadge variant={promoVariant} />
        }
        isLive={isLive}
        isComplete={isWatched}
        isSponsored={isAssetSponsored(asset)}
        subscriptionLabel={getSubscriptionLabel(asset)}
        upcomingShowLabel={upcomingAssetLabel}
        ageLimit={getAssetAgeLimit(asset)}
        duration={duration}
        position={position}
      />
      {!skipMetadata && (
        <div
          className={classNames(styles.titleContainer, {
            [styles.hasIcon]: isExternal,
          })}
        >
          <Label size="small" labels={labels} allcaps />
          <Title title={title} className={styles.title} rootElement="h3" />
          {isExternal && (
            <OpenExternalIcon className={styles.externalLinkIcon} />
          )}
        </div>
      )}
      {!skipMetadata && description && (
        <Description description={description} />
      )}
    </div>
  );

  const className = classNames(styles.assetLink, {
    [styles.isCurrentlyPlaying]: isPlaying,
  });

  if (href) {
    return (
      <a className={className} href={href}>
        {thumbnail}
      </a>
    );
  } else {
    return (
      <AssetLink
        asset={asset}
        className={className}
        onClick={onClick}
        time={position}
        replacePath={replacePath}
      >
        {thumbnail}
      </AssetLink>
    );
  }
};

export const AssetVertical = memo(AssetVerticalBase);
